import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { proBuilderKeys } from '~/lib/query-keys';
import { ACTIVITY_POLL_MS } from '~/hooks/queries/use-coding-sessions';

/**
 * FRONT-3040: the person's one Pro Builder session, as `GET
 * /api/pro-builder/session` answers it. `id` is the session ROW id, not the
 * terminal id. `state` is what Claude Code's own hooks last reported (null
 * when they have not fired yet or were removed); `live` is whether the PTY
 * is still alive; `seen` is true when nothing is unseen.
 */
export type ProBuilderSessionState = 'working' | 'needs-you' | 'finished' | 'failed' | 'idle' | 'ended';

/**
 * FRONT-3077: how much of Claude Code's context its LAST finished model call
 * used — input plus cache tokens, output excluded, the same meaning as the
 * guided Builder's meter — and the model's window (`null` when unknown). Only
 * numbers leave the sandbox, never transcript content.
 */
export interface ProBuilderContextReading {
  tokens: number;
  windowTokens: number | null;
  /** When the reading was written (ISO). */
  at: string;
}

export interface ProBuilderSessionView {
  id: string;
  state: ProBuilderSessionState | null;
  stateAt: string | null;
  live: boolean;
  seen: boolean;
  startedAt: string;
  endedAt: string | null;
  /** FRONT-3077: null until Claude Code has finished a model call in this session. */
  context: ProBuilderContextReading | null;
  /** FRONT-3108: a consent card from Claude Code waits on this live terminal (always false on an ended row). */
  consentWaiting: boolean;
}

export const PRO_BUILDER_SESSION_PATH = '/api/pro-builder/session';

/** 401 (signed out) and 404 (flag off: every Pro Builder route answers the unknown-URL 404) end the poll. */
export function endsSessionPoll(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === 401 || status === 404;
}

/**
 * The board's read of the Pro Builder session, polled on the SAME interval as
 * the coding board (`ACTIVITY_POLL_MS`). Two readers share this one cache
 * entry: the chat top-bar board, and (FRONT-3077) the Pro Builder window's
 * header, whose context meter reads `context` while the window is open.
 * Disabled returns a stable null: a
 * disabled query can still hold another instance's cached rows, so the
 * return is gated on `enabled` rather than trusting `data`.
 */
export function useProBuilderSessionForBoard(enabled: boolean): ProBuilderSessionView | null {
  const { data } = useQuery({
    queryKey: proBuilderKeys.session(),
    queryFn: () => api.get<{ session: ProBuilderSessionView | null }>(PRO_BUILDER_SESSION_PATH),
    enabled,
    refetchInterval: (q) => (endsSessionPoll(q.state.error) ? false : ACTIVITY_POLL_MS),
    retry: (_count, err) => !endsSessionPoll(err),
    select: (response) => response.session,
  });
  return enabled ? (data ?? null) : null;
}
